/**
 * Whether the side rail is expanded or collapsed — a preference that has to survive a reload, and
 * has to be on the document BEFORE React exists.
 *
 * WHY PRE-PAINT MATTERS MORE HERE THAN IT DID FOR THE THEME. The no-FOUC precedent in every app's
 * index.html exists so a stored dark theme does not flash light for one frame. A rail is worse than
 * a flash: in push mode it owns a grid column, so booting expanded and then correcting to collapsed
 * reflows the entire page under the reader's cursor. The attribute is therefore written by the
 * inline script in index.html, and this module is what the React side reads and writes so the two
 * cannot disagree about the key.
 *
 * EVERY ACCESS IS WRAPPED, and not for tidiness. `localStorage` does not merely come back empty in a
 * private window or with site data blocked — the getter itself THROWS. Here that throw would happen
 * during the shell's first render, which blanks every screen in the product for exactly the people
 * least likely to report why. `apps/tenders/src/view-mode.ts` learned this first; this is the same
 * shape.
 */

export const RAIL_STORAGE_KEY = 'huaca-rail';

/**
 * What a module gets before anyone has expressed a preference.
 *
 * BOTH MODES START CLOSED, for different reasons.
 *
 * Overlay has never had a choice: open, it is a modal dialog — scrim over the product, focus
 * trapped inside the nav, page scroll frozen. Defaulting that to open means the first thing a user
 * ever sees of the module is a dialog they did not open and have to dismiss, on every fresh profile.
 *
 * Push was `expanded`, on the argument that a rail already costs a column so it may as well show
 * its destinations. That is superseded by a product decision: the icon column comes first. The
 * labels are one press of the collapse control away and the choice is remembered from then on, so
 * the cost falls on the first visit only.
 *
 * THIS CONSTANT IS NOT THE ONLY COPY. Every app's index.html carries a pre-paint inline script that
 * writes `data-rail` before React exists, and it has to reach the same answer — a rail that boots
 * expanded and is corrected to collapsed reflows the whole page under the reader's cursor, which is
 * precisely what the pre-paint script exists to prevent. ModuleApp.test.tsx pins the two together.
 */
export const DEFAULT_RAIL: Record<RailMode, RailState> = {
  push: 'collapsed',
  overlay: 'collapsed',
};

/**
 * How the rail's EXPANDED panel behaves. Collapsed is a 56px icon column either way.
 *
 * `push` takes a grid column and moves the content across; `overlay` floats over it with a scrim
 * and leaves the layout alone. Overlay is a modal surface — focus trapped, Escape closes — so the
 * two are not interchangeable styling choices.
 */
export const RAIL_MODES = ['push', 'overlay'] as const;
export type RailMode = (typeof RAIL_MODES)[number];

export const RAIL_STATES = ['expanded', 'collapsed'] as const;
export type RailState = (typeof RAIL_STATES)[number];

const isRailState = (value: unknown): value is RailState => RAIL_STATES.includes(value as RailState);

/** The stored preference, or the mode's default — never a throw, never a value outside the union. */
export function readRailState(mode: RailMode = 'push'): RailState {
  try {
    const stored = window.localStorage.getItem(RAIL_STORAGE_KEY);
    // A stale or hand-edited value is dropped rather than applied: an unrecognised string would
    // otherwise reach `data-rail`, where no stylesheet matches it and the rail renders in neither
    // state correctly.
    return isRailState(stored) ? stored : DEFAULT_RAIL[mode];
  } catch {
    return DEFAULT_RAIL[mode];
  }
}

/** Best-effort. A browser that refuses to store it still honours the choice for this session. */
export function writeRailState(state: RailState): void {
  try {
    window.localStorage.setItem(RAIL_STORAGE_KEY, state);
  } catch {
    /* private window, blocked site data, or a full quota — none of which should break the shell */
  }
}

/**
 * Publish the state on the document root, where MODULE stylesheets can see it.
 *
 * Not merely bookkeeping. A push rail narrows the content column while every module breakpoint still
 * measures the VIEWPORT — container queries, the correct tool, are unavailable at the Safari 14
 * floor. tenders already learned the width-only lesson the expensive way: a desktop at 200% zoom
 * reported 720px, took the phone layout, and grew the document to 6271px. `data-rail` is how a
 * module asks about the rail instead of guessing from width.
 */
export function applyRailState(state: RailState, mode: RailMode = 'push'): void {
  const root = document.documentElement;
  root.setAttribute('data-rail', state);
  root.setAttribute('data-rail-mode', mode);
}
